import { Link } from 'react-router-dom';
import { CloudUpload, Loader2, RefreshCw, RotateCw } from 'lucide-react';

import { useViewModelReady } from '../../../../hooks/useViewModel';
import type { GoogleNexusProject, GoogleNexusUploadListItem } from '../../../../domain/nexus';
import { paths } from '../../../../routes/paths';
import { CopyOnClick } from './CopyOnClick';
import { NexusErrorBanner } from './ErrorBanner';
import {
  diagnosticMessage,
  formatNexusBytes,
  formatNexusTimestamp,
} from './helpers';
import { NexusStatusBadge } from './StatusBadge';
import { useGoogleNexusViewModel } from './viewModel';

/** Validation history: one row per request submitted to Nexus. */
export function DeliveriesPanel() {
  const vm = useGoogleNexusViewModel();
  useViewModelReady(vm.initialize);

  if (vm.busy) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">Validation progress for uploads submitted to Google Nexus.</p>
        <div className="flex shrink-0 items-center gap-2">
          {vm.hasActiveUploads && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
              <RotateCw className="h-3 w-3 animate-spin" />
              auto-polling
            </span>
          )}
          <button
            type="button"
            onClick={() => void vm.refresh()}
            disabled={vm.loading}
            aria-label="Refresh"
            className="inline-flex h-10 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${vm.loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {vm.error && <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} />}

      <DeliveryCollections projects={vm.dashboard?.projects ?? []} />
      <UploadHistory uploads={vm.dashboard?.uploads ?? []} />
    </>
  );
}

/** Latest cumulative collection per delivery: what researchers should be given. */
function DeliveryCollections({ projects }: { projects: GoogleNexusProject[] }) {
  const withCollections = projects.filter((project) => !project.archivedAt && project.latestDeliveryCollection);
  if (!withCollections.length) return null;
  return (
    <section className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3 sm:px-5">
        <h2 className="font-semibold text-gray-900">Delivery collections</h2>
        <p className="mt-0.5 text-xs text-gray-500">
          Share these with researchers instead of individual P- project IDs. Each covers every task in the delivery up to its latest upload.
        </p>
      </div>
      <ul className="divide-y divide-gray-100">
        {withCollections.map((project) => (
          <li key={project.id} className="px-4 py-3 text-sm sm:px-5">
            <p className="font-medium text-gray-900">{project.displayName}</p>
            <CopyOnClick
              value={project.latestDeliveryCollection!.latestDeliveryCollectionId}
              label="delivery collection"
              className="mt-0.5 break-all font-mono text-xs text-gray-600"
            >
              {project.latestDeliveryCollection!.latestDeliveryCollectionId}
            </CopyOnClick>
          </li>
        ))}
      </ul>
    </section>
  );
}

function UploadHistory({ uploads }: { uploads: GoogleNexusUploadListItem[] }) {
  return (
    <section className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-3 sm:px-5">
        <h2 className="font-semibold text-gray-900">Validation history</h2>
        <p className="mt-0.5 text-xs text-gray-500">Async Nexus jobs usually take 15–20 minutes.</p>
      </div>
      {uploads.length ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium sm:px-5">Validation</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Archive</th>
                <th className="px-4 py-3 font-medium sm:pr-5">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {uploads.map((upload) => {
                const resultSummary = upload.resultSummary;
                return (
                  <tr key={upload.id} className="align-top">
                    <td className="px-4 py-3 sm:px-5">
                      <div>
                        <Link
                          to={paths.adminNexusUpload(upload.id)}
                          className="font-medium text-primary hover:underline"
                        >
                          {upload.displayName}
                        </Link>
                        {upload.validationFormat && (
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            {upload.validationFormat === 'harbor' ? 'Harbor' : 'Docker Worlds'}
                          </p>
                        )}
                        <p className="mt-0.5 font-mono text-[11px] text-gray-500">
                          {upload.remoteProjectId || upload.id}
                        </p>
                        {upload.latestDeliveryCollectionId && (
                          <CopyOnClick
                            value={upload.latestDeliveryCollectionId}
                            label="delivery collection"
                            className="mt-0.5 max-w-xs break-all font-mono text-[11px] text-gray-500"
                          >
                            {upload.latestDeliveryCollectionId}
                          </CopyOnClick>
                        )}
                        {Boolean(upload.lastPollError) && (
                          <p className="mt-1 max-w-xs text-xs text-amber-700">
                            Poll: {diagnosticMessage(upload.lastPollError)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <NexusStatusBadge status={upload.status} />
                        {resultSummary && resultSummary.total > 0 && (
                          <p className="mt-1 whitespace-nowrap text-[11px] text-gray-500">
                            {resultSummary.passed} passed · {resultSummary.failed} failed
                            {resultSummary.errored ? ` · ${resultSummary.errored} errors` : ''}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <div>
                        <p className="max-w-[14rem] truncate">{upload.archiveName}</p>
                        <p className="mt-0.5 text-gray-400">{formatNexusBytes(upload.archiveSizeBytes)}</p>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500 sm:pr-5">
                      <div>
                        {formatNexusTimestamp(upload.createdAt)}
                        {upload.testRun && <p className="mt-1 text-blue-600">test run</p>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex flex-col items-center px-5 py-10 text-center text-gray-400">
          <CloudUpload className="h-6 w-6" />
          <p className="mt-2 text-sm font-medium text-gray-700">No validation uploads yet</p>
        </div>
      )}
    </section>
  );
}
